import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Pencil, Tag, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  fetchTagAssignments,
  updateTagAssignments,
  type TagAssignmentItem,
} from '@/api/tags';
import { useI18n } from '@/i18n';

type ObjectTagsPanelProps = {
  categoryCode: string;
  objectId: number;
  title?: string;
  emptyText?: string;
  className?: string;
  showTitle?: boolean;
  hideEdit?: boolean;
  onlyModal?: boolean; 
  onClose?: () => void;
  onSaveSuccess?: () => void; // Finalized this prop
};

function TagAssignmentModal({
  title,
  items,
  saving,
  onToggle,
  onSave,
  onClose,
}: {
  title: string;
  items: TagAssignmentItem[];
  saving: boolean;
  onToggle: (tagId: number) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const { t } = useI18n();

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl animate-in zoom-in-95 duration-200"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            <p className="mt-1 text-sm text-slate-500">{t('management.tags.assignmentHint')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
          {items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
              {t('management.tags.noAvailableTags')}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {items.map((item) => {
                const active = item.assigned === 1;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onToggle(item.id)}
                    className={`flex items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${
                      active
                        ? 'border-slate-900 bg-slate-50 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        className="h-3.5 w-3.5 rounded-full border border-white/70 shadow-sm"
                        style={{ backgroundColor: item.color }}
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-800">{item.label}</div>
                        <div className="text-xs font-mono text-slate-400">{item.color}</div>
                      </div>
                    </div>
                    <span
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full border transition ${
                        active
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-300 bg-white text-transparent'
                      }`}
                    >
                      <Check size={14} />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
          >
            {t('management.users.cancel')}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('management.users.save')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function ObjectTagsPanel({
  categoryCode,
  objectId,
  title,
  emptyText,
  hideEdit = false,
  showTitle = true,
  onlyModal = false,
  onClose,
  onSaveSuccess,
  className = '',
}: ObjectTagsPanelProps) {
  const { t } = useI18n();
  const [items, setItems] = useState<TagAssignmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(onlyModal);

  async function loadAssignments() {
    setLoading(true);
    try {
      const response = await fetchTagAssignments(categoryCode, objectId);
      setItems(response);
    } catch {
      toast.error(t('management.tags.assignmentLoadError'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAssignments();
  }, [categoryCode, objectId]);

  const assignedItems = useMemo(
    () => items.filter((item) => item.assigned === 1),
    [items],
  );

  function toggleTag(tagId: number) {
    setItems((prev) =>
      prev.map((item) =>
        item.id === tagId
          ? { ...item, assigned: item.assigned === 1 ? 0 : 1 }
          : item,
      ),
    );
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateTagAssignments(
        categoryCode,
        objectId,
        items.filter((item) => item.assigned === 1).map((item) => item.id),
      );
      toast.success(t('management.tags.assignmentSaveSuccess'));
      setOpen(false);
      
      // If we have onSaveSuccess callback, fire it!
      if (onSaveSuccess) onSaveSuccess();
      
      if (onlyModal && onClose) {
        onClose();
      } else {
        await loadAssignments();
      }
    } catch {
      toast.error(t('management.tags.assignmentSaveError'));
    } finally {
      setSaving(false);
    }
  }

  const handleClose = () => {
    setOpen(false);
    if (onlyModal && onClose) {
      onClose();
    }
  };

  if (onlyModal) {
    return (
      <>
        {loading && (
          <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/5 backdrop-blur-[2px]">
             <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        )}
        {!loading && (
          <TagAssignmentModal
            title={title || t('management.tags.editAssignmentsTitle')}
            items={items}
            saving={saving}
            onToggle={toggleTag}
            onSave={handleSave}
            onClose={handleClose}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className={`rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm ${className}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {showTitle ? (
              <div className="flex items-center gap-2 text-slate-700">
                <Tag className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-semibold">{title ?? t('management.tabs.tags')}</span>
              </div>
            ) : null}
            <div className={showTitle ? 'mt-2 flex flex-wrap gap-2' : 'flex flex-wrap gap-2'}>
              {loading ? (
                <span className="text-sm text-slate-400">{t('common.loading')}</span>
              ) : assignedItems.length > 0 ? (
                assignedItems.map((item) => (
                  <span
                    key={item.id}
                    className="inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-semibold shadow-sm ring-1 ring-black/5"
                    style={{
                      borderColor: item.color,
                      color: item.color,
                      background: `linear-gradient(180deg, rgba(255,255,255,0.94) 0%, ${item.color}22 52%, ${item.color}38 100%)`,
                      boxShadow: `inset 0 1px 1px rgba(255,255,255,0.5), inset 0 -1px 2px rgba(15,23,42,0.12), 0 1px 2px rgba(15,23,42,0.08)`,
                    }}
                  >
                    <span
                      className="inline-flex h-[14px] w-[14px] rounded-full border border-white/70 shadow-sm"
                      style={{
                        background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95) 0%, ${item.color} 42%, ${item.color}DD 72%, rgba(15,23,42,0.24) 100%)`,
                        boxShadow: `inset 0 1px 1px rgba(255,255,255,0.45), inset 0 -1px 2px rgba(15,23,42,0.18), 0 1px 2px rgba(15,23,42,0.12)`,
                      }}
                    />
                    {item.label}
                  </span>
                ))
              ) : (
                <span className="text-sm text-slate-400">{emptyText ?? t('management.tags.noAssignedTags')}</span>
              )}
            </div>
          </div>

          {!hideEdit && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
              title={t('management.tags.editAssignments')}
            >
              <Pencil size={16} />
            </button>
          )}
        </div>
      </div>

      {open ? (
        <TagAssignmentModal
          title={title || t('management.tags.editAssignmentsTitle')}
          items={items}
          saving={saving}
          onToggle={toggleTag}
          onSave={handleSave}
          onClose={handleClose}
        />
      ) : null}
    </>
  );
}
